import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { FilterSelect } from '../../components/ListControls';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import { ModeIcon } from '../shipments/ShipmentCard';
import { SHIPMENTS } from '../shipments/sample';
import { initials } from '../../lib/text';
import { dayName, STATUS_TONE, STATUSES, type ActivityFilters } from './activityFilters';
import { PARTIES, type ActivityEvent } from './sample';

/** The feed, newest first, a table per day */
export function ActivityFeed({ filters }: { filters: ActivityFilters }) {
  const days = [...new Set(filters.shown.map((e) => e.date))];
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <FilterSelect label="Date" value={filters.day} onChange={filters.setDay} options={['Today', 'Yesterday']} />
        <FilterSelect label="Type" value={filters.status} onChange={filters.setStatus} options={STATUSES} />
        <FilterSelect label="Contact" value={filters.party} onChange={filters.setParty} options={PARTIES} />
      </Stack>
      {days.length === 0 && <Panel title="Nothing here">No activity matches.</Panel>}
      {days.map((date) => (
        <Panel key={date} title={dayName(date)} subtitle={dayName(date) === date ? undefined : date}>
          <DataTable<ActivityEvent>
            rows={filters.shown.filter((e) => e.date === date)}
            rowKey={(e) => e.id}
            columns={[
              { header: 'Time', primary: true, nowrap: true, render: (e) => e.time },
              { header: 'Shipment', render: (e) => <ShipmentCell srb={e.srb} /> },
              {
                header: 'Contact',
                render: (e) => (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 12 }}>{initials(e.org)}</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 650, fontSize: 13 }}>{e.party}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.org}
                      </Typography>
                    </Box>
                  </Stack>
                ),
              },
              { header: 'Summary', render: (e) => <span style={{ whiteSpace: 'pre-line' }}>{e.text}</span> },
              {
                header: 'Status',
                nowrap: true,
                render: (e) => <StatusChip label={e.status} tone={STATUS_TONE[e.status]} />,
              },
              {
                header: '',
                action: true,
                render: (e) => (
                  <Button
                    component={RouterLink}
                    to={`/communications/${e.srb}?party=${encodeURIComponent(e.party)}`}
                    variant="outlined"
                    size="small"
                  >
                    View Conversation
                  </Button>
                ),
              },
            ]}
          />
        </Panel>
      ))}
    </Stack>
  );
}

function ShipmentCell({ srb }: { srb: string }) {
  const s = SHIPMENTS.find((x) => x.srb === srb);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {s && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.selected', color: 'text.secondary' }}>
          <ModeIcon mode={s.mode} />
        </Avatar>
      )}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{srb}</Typography>
        {s && (
          <Typography variant="caption" color="text.secondary">
            {s.from.city} → {s.to.city}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
